export type StrokeLocation = 'inside' | 'center' | 'outside';

const INF = 1e9;
/** Chamfer weights: 3 orthogonal / 4 diagonal approximate Euclidean distance within ~2%. */
const ORTHOGONAL = 3;
const DIAGONAL = 4;
const SCALE = 1 / ORTHOGONAL;

/**
 * Distance from every pixel to the nearest background pixel, in pixels. A
 * two-pass chamfer transform is linear in the pixel count, which matters because
 * this runs on a whole document-sized mask.
 */
export function distanceToBackground(
  mask: Uint8Array,
  width: number,
  height: number,
  threshold = 128,
): Float32Array {
  const distance = new Float32Array(width * height);
  for (let i = 0; i < distance.length; i += 1) {
    distance[i] = mask[i] >= threshold ? INF : 0;
  }

  const relax = (index: number, from: number, cost: number): void => {
    const candidate = distance[from] + cost;
    if (candidate < distance[index]) distance[index] = candidate;
  };

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = y * width + x;
      if (distance[index] === 0) continue;
      if (y > 0) {
        relax(index, index - width, ORTHOGONAL);
        if (x > 0) relax(index, index - width - 1, DIAGONAL);
        if (x + 1 < width) relax(index, index - width + 1, DIAGONAL);
      }
      if (x > 0) relax(index, index - 1, ORTHOGONAL);
    }
  }

  for (let y = height - 1; y >= 0; y -= 1) {
    for (let x = width - 1; x >= 0; x -= 1) {
      const index = y * width + x;
      if (distance[index] === 0) continue;
      if (y + 1 < height) {
        relax(index, index + width, ORTHOGONAL);
        if (x > 0) relax(index, index + width - 1, DIAGONAL);
        if (x + 1 < width) relax(index, index + width + 1, DIAGONAL);
      }
      if (x + 1 < width) relax(index, index + 1, ORTHOGONAL);
    }
  }

  for (let i = 0; i < distance.length; i += 1) {
    distance[i] = Math.min(distance[i] * SCALE, INF);
  }
  return distance;
}

function invertMask(mask: Uint8Array, threshold: number): Uint8Array {
  const out = new Uint8Array(mask.length);
  for (let i = 0; i < mask.length; i += 1) out[i] = mask[i] >= threshold ? 0 : 255;
  return out;
}

/**
 * Coverage of a band along the selection edge, ready to be uploaded as an R8
 * texture and painted through. The band is antialiased over its last pixel so
 * the result does not look like a staircase on curves.
 */
export function strokeCoverage(
  mask: Uint8Array,
  width: number,
  height: number,
  thickness: number,
  location: StrokeLocation,
  threshold = 128,
): Uint8Array {
  const coverage = new Uint8Array(width * height);
  if (thickness <= 0) return coverage;

  const inner = location === 'center' ? thickness / 2 : location === 'inside' ? thickness : 0;
  const outer = location === 'center' ? thickness / 2 : location === 'outside' ? thickness : 0;

  const insideDistance = inner > 0 ? distanceToBackground(mask, width, height, threshold) : null;
  const outsideDistance =
    outer > 0 ? distanceToBackground(invertMask(mask, threshold), width, height, 128) : null;

  for (let i = 0; i < coverage.length; i += 1) {
    const selected = mask[i] >= threshold;
    const distance = selected ? insideDistance?.[i] : outsideDistance?.[i];
    const limit = selected ? inner : outer;
    if (distance === undefined || limit <= 0) continue;
    // Distances are measured from the far side of the edge, hence the +1.
    const alpha = Math.min(1, Math.max(0, limit - distance + 1));
    coverage[i] = Math.round(alpha * 255);
  }
  return coverage;
}
